import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, TextInput, View } from 'react-native';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { Button, Text } from '@/components/ui';
import {
  createConnection,
  fetchConnections,
  revokeConnection,
  type ConnectionRecord,
  type ConnectionScope,
  type CreatedConnection,
} from '@/lib/connections/api';

const PAT_PREFIX_FALLBACK = 'cs_pat_';

function formatWhen(value: string | null): string {
  if (!value) {
    return 'No expiry';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString();
}

function cursorSnippet(created: CreatedConnection): string {
  return JSON.stringify(
    {
      mcpServers: {
        couplespace: {
          url: created.endpoint,
          headers: { Authorization: `Bearer ${created.token}` },
        },
      },
    },
    null,
    2,
  );
}

function grokSnippet(created: CreatedConnection): string {
  return `grok mcp add --transport http couplespace "${created.endpoint}" --header "Authorization: Bearer ${created.token}"`;
}

export default function ConnectionsScreen() {
  const [connections, setConnections] = useState<ConnectionRecord[]>([]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ConnectionScope>('read');
  const [expiresInDays, setExpiresInDays] = useState('90');
  const [created, setCreated] = useState<CreatedConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setConnections(await fetchConnections());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(): Promise<void> {
    const trimmed = name.trim();
    const days = Number(expiresInDays);
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      setError('Expiry must be between 1 and 365 days');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await createConnection({ name: trimmed, scope, expiresInDays: days });
      setCreated(result);
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setSaving(false);
    }
  }

  function onRevoke(row: ConnectionRecord): void {
    const message = `Revoke "${row.name ?? 'token'}"? Clients using it will stop working immediately.`;
    const run = () => {
      void revokeConnection(row.id)
        .then(() => load())
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Failed to revoke token');
        });
    };
    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        run();
      }
      return;
    }
    run();
  }

  async function copyText(value: string): Promise<void> {
    if (Platform.OS === 'web' && navigator.clipboard) {
      await navigator.clipboard.writeText(value);
    }
  }

  return (
    <>
      <DocumentTitle title="Connections" />
      <ScrollView className="flex-1 bg-surface" contentContainerClassName="px-page py-page-y">
        <Text variant="h1">Connections</Text>
        <Text className="mt-2 max-w-xl text-muted">
          Personal access tokens let Cursor, Grok, and other MCP clients read or edit your workspace
          as you. A token is shown once. Write includes moving pages to trash.
        </Text>

        <Text variant="caption" className="mt-6 text-muted">
          Name
        </Text>
        <TextInput
          className="mt-1 rounded-lg border border-border bg-surface px-3 py-2 text-base text-fg"
          onChangeText={setName}
          placeholder="Cursor on my laptop"
          value={name}
        />

        <View className="mt-4 flex-row gap-2">
          <Pressable
            className={`rounded-lg border px-3 py-2 ${scope === 'read' ? 'border-accent bg-accent' : 'border-border'}`}
            onPress={() => setScope('read')}
          >
            <Text className={scope === 'read' ? 'text-fg-inverse' : 'text-fg'}>Read</Text>
          </Pressable>
          <Pressable
            className={`rounded-lg border px-3 py-2 ${scope === 'write' ? 'border-accent bg-accent' : 'border-border'}`}
            onPress={() => setScope('write')}
          >
            <Text className={scope === 'write' ? 'text-fg-inverse' : 'text-fg'}>
              Read and write
            </Text>
          </Pressable>
        </View>

        <Text variant="caption" className="mt-4 text-muted">
          Expires in days (1–365)
        </Text>
        <TextInput
          className="mt-1 w-24 rounded-lg border border-border bg-surface px-3 py-2 text-base text-fg"
          keyboardType="number-pad"
          onChangeText={setExpiresInDays}
          value={expiresInDays}
        />

        <Button
          className="mt-4 self-start"
          disabled={saving}
          label={saving ? 'Creating…' : 'Create token'}
          onPress={() => void onCreate()}
        />

        {error ? <Text className="mt-3 text-sm text-danger">{error}</Text> : null}

        {created ? (
          <View className="mt-6 max-w-xl rounded-lg border border-border p-4">
            <Text className="text-fg">Copy this token now. It will not be shown again.</Text>
            <Text className="mt-3 font-mono text-sm text-fg">{created.token}</Text>
            <Button
              className="mt-3 self-start"
              label="Copy token"
              variant="secondary"
              onPress={() => void copyText(created.token)}
            />
            <Text variant="caption" className="mt-4 text-muted">
              Cursor mcp.json
            </Text>
            <Text className="mt-1 font-mono text-xs text-fg">{cursorSnippet(created)}</Text>
            <Text variant="caption" className="mt-4 text-muted">
              Grok CLI
            </Text>
            <Text className="mt-1 font-mono text-xs text-fg">{grokSnippet(created)}</Text>
          </View>
        ) : null}

        <Text variant="h2" className="mt-8">
          Tokens
        </Text>
        {loading ? (
          <ActivityIndicator className="mt-4" />
        ) : connections.length === 0 ? (
          <Text className="mt-3 text-muted">No tokens yet.</Text>
        ) : (
          connections.map((row) => (
            <View
              key={row.id}
              className="mt-3 max-w-xl flex-row items-center justify-between gap-3"
            >
              <View className="flex-1">
                <Text className="text-fg">{row.name ?? 'Untitled token'}</Text>
                <Text variant="caption" className="text-muted">
                  {row.start ?? PAT_PREFIX_FALLBACK} ·{' '}
                  {row.scope === 'write' ? 'Read and write' : 'Read'} · expires{' '}
                  {formatWhen(row.expiresAt)}
                  {row.memberName ? ` · ${row.memberName}` : ''}
                </Text>
              </View>
              <Pressable onPress={() => onRevoke(row)}>
                <Text className="text-danger">Revoke</Text>
              </Pressable>
            </View>
          ))
        )}
      </ScrollView>
    </>
  );
}
